import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import Layout from "../../component/layout";
import { toastify } from "../../../../utils/toast";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";
import { getItem } from "../../../../utils/localStorage";

const AddPurchaseOrder = () => {
  const navigate = useNavigate();
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // Form fields
  const [currency, setCurrency] = useState("KES");
  const [supplier, setSupplier] = useState(null);
  const [department, setDepartment] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [supplierNotes, setSupplierNotes] = useState("");
  const [prNo, setPrNo] = useState("");
  const [approvers, setApprovers] = useState([]);
  const [selectedApprovers, setSelectedApprovers] = useState([]);

  // Current user state
  const [currentUser, setCurrentUser] = useState(null);
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");

  // Items array
  const [items, setItems] = useState([]);

  // Options data
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [budgetOptions, setBudgetOptions] = useState([]);

  // Loading & submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch user data and initial data when component mounts
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUserValue = await getItem("APPUSER");
        let parsedUser;
        console.log("+++++++++++++++++++++++++++++++++++++")
        console.log("+++++++++++++++++++++++++++++++++++++")
        console.log("Current User Value:", currentUserValue);
        console.log("+++++++++++++++++++++++++++++++++++++")
        console.log("+++++++++++++++++++++++++++++++++++++")

        // Check if the value needs to be parsed
        if (typeof currentUserValue === "string") {
          try {
            parsedUser = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
          }
        } else {
          parsedUser = currentUserValue; // Assume it's already an object
        }

        // Safely access and set user details
        if (parsedUser?.user) {
          const fullName = parsedUser.user.fullName;
          const role = parsedUser.user.role;

          setCurrentUser(parsedUser.user);
          setFullName(fullName);
          setRole(role);
        } else {
          console.error("Invalid user structure:", parsedUser);
        }
      } catch (err) {
        console.log(err.message);
      }
    };

    getCurrentUser();
  }, []);

  // Fetch data when facility is selected
  useEffect(() => {
    if (!selectedFacilityId) return;

    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        // Fetch suppliers
        const suppliersResponse = await makeRequest2(
          `/api/app/suppliers/${selectedFacilityId}`,
          "GET"
        );
        if (suppliersResponse.success && Array.isArray(suppliersResponse.data)) {
          setSupplierOptions(suppliersResponse.data);
        }

        // Fetch departments
        const departmentsResponse = await makeRequest2(
          `/api/app/departments/${selectedFacilityId}`,
          "GET"
        );
        if (departmentsResponse.success && Array.isArray(departmentsResponse.data)) {
          setDepartmentOptions(departmentsResponse.data);
        }

        // Fetch budgets
        const budgetsResponse = await makeRequest2(
          `/api/app/budgets/${selectedFacilityId}`,
          "GET"
        );
        if (budgetsResponse.success && Array.isArray(budgetsResponse.data)) {
          setBudgetOptions(budgetsResponse.data);
        }

        // Fetch approvers
        const approversResponse = await makeRequest2(
          `/api/app/users/approvers/${selectedFacilityId}`,
          "GET"
        );
        if (approversResponse.success && Array.isArray(approversResponse.data)) {
          setApprovers(approversResponse.data);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toastify("Error fetching initial data", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [selectedFacilityId]);

  // If no facility is selected, display a message and exit early.
  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="text-center p-5">Please select a facility first.</div>
      </Layout>
    );
  }

  // Add a new item row
  const handleAddItem = () => {
    setItems([
      ...items,
      {
        description: "",
        budget: null,
        unitPrice: 0,
        quantity: 1,
        taxRate: 0,
        total: 0,
      },
    ]);
  };

  // Calculate item total
  const calculateItemTotal = (unitPrice, quantity, taxRate) => {
    const subtotal = parseFloat(unitPrice) * parseFloat(quantity);
    const tax = subtotal * (parseFloat(taxRate) / 100);
    return subtotal + tax;
  };

  // Handle changes for each item field
  const handleItemChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };

          // Recalculate total if price, quantity or tax rate changes
          if (field === "unitPrice" || field === "quantity" || field === "taxRate") {
            updatedItem.total = calculateItemTotal(
              field === "unitPrice" ? value : item.unitPrice,
              field === "quantity" ? value : item.quantity,
              field === "taxRate" ? value : item.taxRate
            );
          }

          return updatedItem;
        }
        return item;
      })
    );
  };

  // Remove an item row
  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Toggle approver selection
  const handleApproverToggle = (approverId) => {
    setSelectedApprovers((prev) => {
      if (prev.includes(approverId)) {
        return prev.filter((id) => id !== approverId);
      } else {
        return [...prev, approverId];
      }
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    const grossTotal = items.reduce((sum, item) => sum + parseFloat(item.total || 0), 0);
    const totalTax = items.reduce(
      (sum, item) =>
        sum + (parseFloat(item.unitPrice || 0) * parseFloat(item.quantity || 0) * parseFloat(item.taxRate || 0) / 100),
      0
    );

    return {
      grossTotal: grossTotal.toFixed(4),
      totalTax: totalTax.toFixed(4),
      netTotal: (grossTotal - totalTax).toFixed(4)
    };
  };

  // Clear the form
  const handleClear = () => {
    setCurrency("KES");
    setSupplier(null);
    setDepartment("");
    setInternalNotes("");
    setSupplierNotes("");
    setPrNo("");
    setSelectedApprovers([]);
    setItems([]);
  };

  // Submit the purchase order
  const handleSubmit = async () => {
    if (items.length === 0) {
      toastify("Please add at least one item", "error");
      return;
    }

    if (!supplier) {
      toastify("Please select a supplier", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const totals = calculateTotals();

      const payload = {
        facilityId: selectedFacilityId,
        currency,
        supplierId: supplier,
        department,
        internalNotes,
        supplierNotes,
        prNo,
        approverIds: selectedApprovers,
        items: items.map((item) => ({
          description: item.description,
          budgetId: item.budget,
          unitPrice: item.unitPrice,
          quantity: item.quantity,
          taxRate: item.taxRate,
          total: item.total
        })),
        grossTotal: totals.grossTotal,
        totalTax: totals.totalTax,
        netTotal: totals.netTotal
      };

      const response = await makeRequest2(
        "/api/app/purchase-orders",
        "POST",
        payload
      );

      if (!response.success) {
        toastify(response.error || "Failed to create purchase order", "error");
        setIsSubmitting(false);
        return;
      }

      toastify(response.message || "Purchase Order added successfully", "success");
      handleClear();
      navigate("/facility/procurement_management/purchase_orders");
    } catch (error) {
      toastify("An unexpected error occurred: " + (error.message || "Unknown error"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Layout>
      {/* Page Header & Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/procurement_management/purchase_orders">
                    Purchase Orders
                  </Link>
                </li>
                <li className="breadcrumb-item">New Purchase Order</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="/facility/procurement_management/purchase_orders">
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5>New Purchase Order</h5>
              {fullName && (
                <div className="text-muted">
                  From: {fullName} {role ? `(${role})` : ''}
                </div>
              )}
            </div>
            <div className="card-body">
              {/* Loading indicator */}
              {isLoading && (
                <div className="text-center mb-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading data...</p>
                </div>
              )}

              {/* Main Form Fields */}
              <div className="mb-3">
                <label className="form-label">Currency:</label>
                <InputText
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-100"
                  disabled={isSubmitting}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Supplier:</label>
                <Dropdown
                  value={supplier}
                  options={supplierOptions.map((sup) => ({
                    label: sup.name,
                    value: sup.id,
                  }))}
                  onChange={(e) => setSupplier(e.value)}
                  placeholder="Search Supplier"
                  className="w-100"
                  disabled={isSubmitting || isLoading}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Department:</label>
                <InputText
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  className="w-100"
                  disabled={isSubmitting}
                />
              </div>

              <div className="mb-4">
                <label className="form-label">Internal notes:</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div className="mb-4">
                <label className="form-label">PR No.</label>
                <InputText
                  value={prNo}
                  onChange={(e) => setPrNo(e.target.value)}
                  className="w-100"
                  disabled={isSubmitting}
                />
              </div>

              {/* Items Section */}
              <h5>ITEM(S)</h5>
              <div className="mb-3">
                <button
                  className="btn btn-primary"
                  onClick={handleAddItem}
                  disabled={isSubmitting}>
                  Add Item
                </button>
              </div>

              {/* Items Table */}
              <div className="table-responsive mb-4">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Item Description <span className="text-danger">*</span></th>
                      <th>Budget</th>
                      <th>Unit Price</th>
                      <th>Qty</th>
                      <th>Tax Rate</th>
                      <th>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        {/* Item Description */}
                        <td>
                          <textarea
                            className="form-control"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, "description", e.target.value)}
                            rows={2}
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Budget */}
                        <td>
                          <Dropdown
                            value={item.budget}
                            options={budgetOptions.map((budget) => ({
                              label: budget.name,
                              value: budget.id,
                            }))}
                            onChange={(e) => handleItemChange(index, "budget", e.value)}
                            placeholder="-- Select Budget --"
                            className="w-100"
                            disabled={isSubmitting || isLoading}
                          />
                        </td>

                        {/* Unit Price */}
                        <td>
                          <InputNumber
                            value={item.unitPrice}
                            onValueChange={(e) => handleItemChange(index, "unitPrice", e.value)}
                            mode="decimal"
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            className="w-100"
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Quantity */}
                        <td>
                          <InputNumber
                            value={item.quantity}
                            onValueChange={(e) => handleItemChange(index, "quantity", e.value)}
                            min={1}
                            className="w-100"
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Tax Rate */}
                        <td>
                          <InputNumber
                            value={item.taxRate}
                            onValueChange={(e) => handleItemChange(index, "taxRate", e.value)}
                            suffix="%"
                            className="w-100"
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Total */}
                        <td>
                          {currency} {parseFloat(item.total || 0).toFixed(4)}
                        </td>

                        {/* Remove Button */}
                        <td className="text-center">
                          <Button
                            icon="ti ti-trash"
                            className="p-button-rounded p-button-danger mr-2"
                            onClick={() => handleRemoveItem(index)}
                            disabled={isSubmitting}
                          />
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center">
                          No items added. Click "ADD ITEM" to begin.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="row mb-4 justify-content-end">
                <div className="col-md-6">
                  <div className="d-flex justify-content-between mb-2">
                    <div>Gross Total:</div>
                    <div>{currency} {totals.grossTotal}</div>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <div>Total Tax:</div>
                    <div>{currency} {totals.totalTax}</div>
                  </div>
                  <div className="d-flex justify-content-between">
                    <div>Net total:</div>
                    <div>{currency} {totals.netTotal}</div>
                  </div>
                </div>
              </div>

              {/* Additional Sections in Row */}
              <div className="row">
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header bg-light">
                      <h6>SUPPLIER NOTES</h6>
                    </div>
                    <div className="card-body">
                      <textarea
                        className="form-control"
                        rows={5}
                        value={supplierNotes}
                        onChange={(e) => setSupplierNotes(e.target.value)}
                        disabled={isSubmitting}
                      />
                      <div className="mt-3">
                        <p>Select or drop files.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header bg-light">
                      <h6>APPROVERS</h6>
                    </div>
                    <div className="card-body">
                      {approvers.map((approver) => (
                        <div key={approver.id} className="mb-2">
                          <input
                            type="checkbox"
                            id={`approver-${approver.id}`}
                            checked={selectedApprovers.includes(approver.id)}
                            onChange={() => handleApproverToggle(approver.id)}
                            className="me-2"
                            disabled={isSubmitting}
                          />
                          <label htmlFor={`approver-${approver.id}`}>
                            {approver.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="text-end mt-4">
                <Button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  onClick={handleClear}
                  disabled={isSubmitting}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting || items.length === 0}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddPurchaseOrder;